<script setup>
import { computed } from "vue";
import { useRoute } from "vue-router";
import DBreadcrumb from "./DBreadcrumb.vue";

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  subtitle: {
    type: String,
    default: "",
  },
  buttonText: {
    type: String,
    default: "Add New",
  },
  showButton: {
    type: Boolean,
    default: false,
  },
  // Explicit breadcrumb items take priority over route.meta.breadcrumb —
  // pass this when the trail depends on runtime data (e.g. a loaded entity name).
  breadcrumbs: {
    type: Array,
    default: null,
  },
});

const route = useRoute();
const breadcrumbItems = computed(() => props.breadcrumbs || route.meta?.breadcrumb || []);

const emit = defineEmits(["action-click"]);

const handleClick = () => {
  emit("action-click");
};
</script>

<template>
  <div class="page-header-wrap">
    <DBreadcrumb v-if="breadcrumbItems.length" :items="breadcrumbItems" />
    <header class="page-header-component">
      <div class="page-header-component__text">
        <h1 class="page-title">{{ title }}</h1>
        <p v-if="subtitle" class="page-subtitle">{{ subtitle }}</p>
      </div>
      <div class="actions-container">
        <slot name="actions">
          <button v-if="showButton" class="btn-primary" @click="handleClick">
            <!-- Slot for icon or default plus icon -->
            <slot name="icon">
              <i class="bi bi-plus-lg"></i>
            </slot>
            {{ buttonText }}
          </button>
        </slot>
      </div>
    </header>
  </div>
</template>

<style scoped>
/* ===========================================================
   Unified page header — ports the Overview header's visual
   signature (heavy, tight title · small muted subtitle ·
   bottom divider rule) into the shared component so every
   page reads consistently. Token-based → themes light/dark.
   =========================================================== */
.page-header-wrap {
  display: contents;
}

.page-header-component {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding-bottom: 16px;
  margin-bottom: 24px;
  border-bottom: 1px solid var(--neutral-200);
}

.page-header-component__text {
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
  flex: 1;
}

.page-title {
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.15;
  margin: 0;
  color: var(--neutral-900);
}

.page-subtitle {
  color: var(--neutral-500);
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  width: 100%;
}

.actions-container {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.btn-primary {
  background-color: var(--primary-color);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.2s ease, transform 0.15s ease;
}

.btn-primary:hover {
  background-color: var(--primary-hover);
  transform: translateY(-1px);
}

/* ── Responsive ────────────────────────────────────────────── */
@media (max-width: 768px) {
  .page-header-component {
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 16px;
  }

  .page-title {
    font-size: 19px;
  }

  .page-subtitle {
    font-size: 12px;
  }

  .actions-container {
    width: 100%;
  }

  .btn-primary {
    width: 100%;
    justify-content: center;
  }
}

/* ── Dark mode ─────────────────────────────────────────────── */
[data-theme="dark"] .page-header-component {
  border-bottom-color: #2d3f5a;
}

[data-theme="dark"] .page-title {
  color: #e8edf5;
}

[data-theme="dark"] .page-subtitle {
  color: #9aadc4;
}
</style>
